import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { usePortal } from '../../contexts/PortalContext';
import { submitAccountTransfer } from '../../lib/accountTransfer';
import { formatTransferScope } from '../../lib/accountTransferScope';
import { fetchSalespersonEmails } from '../../lib/applicationFormSettings';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { logPortalActivityQuiet } from '../../lib/portalActivityLog';
import type { AccountTransferRequest, Client } from '../../types/crm';
import { isRootAccount, isSubCompany } from '../../utils/accountHierarchy';
import { compareSalespersonCodes, formatSalespersonCode, normalizeSalespersonCodes } from '../../utils/salespersonScope';

interface AccountTransferModalProps {
  open: boolean;
  client: Client;
  currentOwnerRef?: string;
  subCompanyCount?: number;
  onClose: () => void;
  onSubmitted: () => void;
}

export function AccountTransferModal({
  open,
  client,
  currentOwnerRef,
  subCompanyCount = 0,
  onClose,
  onSubmitted,
}: AccountTransferModalProps) {
  const { kind: portal } = usePortal();

  const [codeOptions, setCodeOptions] = useState<string[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);

  const currentNorm = useMemo(
    () => normalizeSalespersonCodes([currentOwnerRef ?? ''])[0] ?? '',
    [currentOwnerRef],
  );

  const candidates = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const raw of codeOptions) {
      const code = formatSalespersonCode(raw);
      if (!code) continue;
      const key = normalizeSalespersonCodes([code])[0]?.toLowerCase() ?? '';
      if (!key || (currentNorm && key === currentNorm.toLowerCase())) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push(code);
    }
    return list.sort(compareSalespersonCodes);
  }, [codeOptions, currentNorm]);

  const isParent = isRootAccount(client);
  const isBrand = isSubCompany(client);

  const [toOwnerId, setToOwnerId] = useState('');
  const [message, setMessage] = useState('');
  const [includeSubCompanies, setIncludeSubCompanies] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setToOwnerId('');
    setMessage('');
    setIncludeSubCompanies(isParent && subCompanyCount > 0);
    setError(null);
  }, [open, isParent, subCompanyCount]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCodesLoading(true);
    void fetchSalespersonEmails()
      .then((rows) => {
        if (cancelled) return;
        setCodeOptions(
          rows
            .filter((row) => row.active)
            .map((row) => row.code.trim())
            .filter(Boolean),
        );
      })
      .catch(() => {
        if (!cancelled) setCodeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const previewScope = formatTransferScope({
    include_sub_companies: includeSubCompanies,
    include_projects: false,
    include_tasks: false,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!toOwnerId) {
      setError('Select the new salesperson code.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await submitAccountTransfer(client.id, toOwnerId, {
        message,
        includeSubCompanies: isParent ? includeSubCompanies : false,
        includeProjects: false,
        includeTasks: false,
      });
      logPortalActivityQuiet({
        portal,
        action: 'transfer',
        entityType: 'transfer',
        entityId: client.id,
        clientId: client.id,
        title: `Transfer requested: ${client.company_name}`,
        detail: [
          `From ${currentOwnerRef || '—'} to ${toOwnerId}.`,
          `Scope: ${previewScope}.`,
          message.trim() ? `Message: ${message.trim()}` : null,
        ]
          .filter(Boolean)
          .join('\n'),
      });
      onSubmitted();
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Transfer account · ${client.company_name}`} open={open} onClose={onClose}>
      <form className="crm-form" onSubmit={(e) => void handleSubmit(e)}>
        <p className="field-hint">
          Submit a transfer request for Admin, Management or IT approval. This changes only the
          account&apos;s <strong>salesperson code</strong>. Users assigned that code can view the
          account afterwards; contacts remain on the account.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <label className="field">
          <span className="field-label">Current salesperson</span>
          <input
            className="field-input mono"
            value={formatSalespersonCode(currentOwnerRef ?? '') || '—'}
            disabled
            readOnly
          />
        </label>

        <label className="field">
          <span className="field-label">New salesperson code *</span>
          <select
            className="field-input"
            value={toOwnerId}
            disabled={saving || codesLoading || candidates.length === 0}
            onChange={(e) => setToOwnerId(e.target.value)}
          >
            <option value="">{codesLoading ? 'Loading codes…' : 'Select salesperson code'}</option>
            {candidates.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
          {!codesLoading && candidates.length === 0 ? (
            <p className="field-hint">
              No other salesperson codes are available. Add codes under User management → Salesperson.
            </p>
          ) : null}
        </label>

        {isParent && subCompanyCount > 0 ? (
          <label className="settings-sharepoint-form__toggle account-transfer-option">
            <input
              type="checkbox"
              checked={includeSubCompanies}
              onChange={(e) => setIncludeSubCompanies(e.target.checked)}
            />
            <span>Include all sub-companies / brands ({subCompanyCount})</span>
          </label>
        ) : null}

        {isBrand ? (
          <p className="field-hint">This is a brand / sub-company. Only this record will be transferred.</p>
        ) : null}

        <p className="field-hint">Scope preview: {previewScope}</p>

        <label className="field">
          <span className="field-label">Message (optional)</span>
          <textarea
            className="field-input field-textarea"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
          />
        </label>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || !toOwnerId}>
            {saving ? 'Submitting…' : 'Submit transfer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function pendingTransferForClient(
  requests: AccountTransferRequest[],
  clientId: string,
): AccountTransferRequest | undefined {
  return requests.find(
    (r) =>
      (r.status ?? '').toLowerCase() === 'pending' &&
      String(r.client_id) === String(clientId),
  );
}
